import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, serverTimestamp, where, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaDownload, FaEnvelope, FaClock, FaUser, FaEdit } from 'react-icons/fa';
import { createNotification, NotificationType } from '../../services/NotificationService';

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [statusFilter, setStatusFilter] = useState('all');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const requestsRef = collection(db, 'requests');
      let q = query(requestsRef, orderBy('createdAt', 'desc'));
      
      // Filtre varsa uygula
      if (statusFilter && statusFilter !== 'all') {
        q = query(requestsRef, 
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const requestsData = [];
      
      for (const doc of snapshot.docs) {
        const request = { id: doc.id, ...doc.data() };
        // Kullanıcı bilgilerini al
        const userDoc = await getDoc(doc(db, 'users', request.userId));
        if (userDoc.exists()) {
          request.user = userDoc.data();
        }
        requestsData.push(request);
      }

      console.log(`${requestsData.length} talep yüklendi`);
      setRequests(requestsData);
    } catch (error) {
      console.error('Talepler yüklenirken hata:', error);
      setError('Talepler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'editor')) {
      fetchRequests();
    } else {
      navigate('/');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, statusFilter]);

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'requests', requestId);
      const request = requests.find(r => r.id === requestId);
      
      await updateDoc(requestRef, {
        status: newStatus,
        lastUpdated: serverTimestamp()
      });

      // Kullanıcıya bildirim gönder
      await createNotification(request.userId, NotificationType.REQUEST_STATUS_CHANGED, {
        requestId: requestId,
        requestTitle: request.title,
        newStatus: newStatus,
        updatedBy: currentUser.email
      });

      // Listeyi güncelle
      setRequests(prevRequests =>
        prevRequests.map(request =>
          request.id === requestId
            ? { ...request, status: newStatus, lastUpdated: new Date() }
            : request
        )
      );

      alert('Durum başarıyla güncellendi ve kullanıcıya bildirim gönderildi.');
    } catch (err) {
      console.error('Durum güncellenirken hata:', err);
      alert('Durum güncellenirken bir hata oluştu.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inProgress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'inProgress': return 'İşleme Alındı';
      case 'completed': return 'Tamamlandı';
      case 'rejected': return 'Reddedildi';
      default: return 'Bilinmiyor';
    }
  };

  const downloadBase64File = (base64Data, fileName) => {
    try {
      // Base64 verinin başındaki meta verileri kaldır (örn: "data:application/pdf;base64,")
      const base64Content = base64Data.split(',')[1] || base64Data;
      
      // Base64'ü Blob'a çevir
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      // Dosyayı indir
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Dosya indirilirken hata:', err);
      alert('Dosya indirilirken bir hata oluştu.');
    }
  };

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'editor')) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Tüm Özel İstekler</h1>
        <div className="text-sm text-gray-500">
          Toplam: {requests.length} istek
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Başlık & Detaylar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dosyalar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer" 
                         onClick={() => navigate(`/admin/requests/${request.id}`)}>
                      {request.title}
                      <div className="text-sm text-gray-500 mt-1">
                        {request.description.substring(0, 100)}...
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Tür: {request.type}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FaUser className="text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900">{request.userEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative group">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)} cursor-pointer`}>
                        {getStatusText(request.status)}
                      </span>
                      <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg hidden group-hover:block z-10">
                        <div className="py-1">
                          <button onClick={() => updateRequestStatus(request.id, 'pending')} 
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            Beklemede
                          </button>
                          <button onClick={() => updateRequestStatus(request.id, 'inProgress')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            İşleme Al
                          </button>
                          <button onClick={() => updateRequestStatus(request.id, 'completed')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            Tamamlandı
                          </button>
                          <button onClick={() => updateRequestStatus(request.id, 'rejected')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            Reddet
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2">
                      {request.files?.map((file, index) => (
                        <button
                          key={index}
                          onClick={() => downloadBase64File(file.base64, file.name)}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <FaDownload className="mr-2" />
                          {file.name}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      <div className="flex items-center mb-1">
                        <FaClock className="mr-2" />
                        {request.createdAt?.toLocaleDateString()}
                      </div>
                      <div className="flex items-center text-xs">
                        <FaEnvelope className="mr-2" />
                        {request.messages?.length || 0} mesaj
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/admin/requests/${request.id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminRequests; 